// src/features/tournament/useTournamentTimerActions.js
import { useCallback } from 'react';
import timeSyncService from '../../services/TimeSyncService';
import { useNotification } from '../../contexts/NotificationContext';
import { useLocale } from '../../contexts/LocaleContext';

export const useTournamentTimerActions = ({
  stateRef,
  setTournamentState,
  syncTimerToDB,
  startWorker,
  stopWorker,
  resetWorker,
  timeStringToSeconds
}) => {
  const { showNotification } = useNotification();
  const { t } = useLocale();

  const handleSyncResult = useCallback((result) => {
    if (!result || !result.success) {
      const errorMsg = result?.error?.message || (typeof result?.error === 'object' ? JSON.stringify(result.error) : result?.error) || 'Unknown error';
      showNotification(`${t('errors.updateFailed')}: ${errorMsg}`, 'error');
    }
  }, [showNotification, t]);

  const startTimer = useCallback(async () => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const remaining = currentState.timer_state.remaining;
    const endsAt = new Date(timeSyncService.getNow() + remaining * 1000).toISOString();

    const newTimerState = {
      ...currentState.timer_state,
      state: 'play',
      hasTournamentStarted: true,
      endsAt: endsAt,
      pausedAt: null
    };

    setTournamentState(prev => ({ ...prev, timer_state: newTimerState }));
    const result = await syncTimerToDB(newTimerState);
    handleSyncResult(result);
    startWorker(remaining);
  }, [setTournamentState, syncTimerToDB, startWorker, stateRef, handleSyncResult]);

  const pauseTimer = useCallback(async () => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const remaining = currentState.timer_state.remaining;
    const newTimerState = {
      ...currentState.timer_state,
      state: 'pause',
      pausedAt: remaining,
      endsAt: null
    };

    setTournamentState(prev => ({ ...prev, timer_state: newTimerState }));
    const result = await syncTimerToDB(newTimerState);
    handleSyncResult(result);
    stopWorker();
  }, [setTournamentState, syncTimerToDB, stopWorker, stateRef, handleSyncResult]);

  const resetTimer = useCallback(async (initialLevelTime) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const newTimerState = {
      state: 'ready',
      remaining: initialLevelTime,
      rowIndex: 0,
      hasTournamentStarted: false,
      pausedAt: initialLevelTime,
      endsAt: null
    };

    setTournamentState(prev => ({
      ...prev,
      timer_state: { ...prev.timer_state, ...newTimerState }
    }));
    const result = await syncTimerToDB(newTimerState);
    handleSyncResult(result);
    resetWorker(initialLevelTime);
  }, [setTournamentState, syncTimerToDB, resetWorker, stateRef, handleSyncResult]);

  const adjustTimer = useCallback(async (seconds) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const { remaining, rowIndex, state } = currentState.timer_state;
    const { levels: currentRows } = currentState.structure;

    let calculatedRemainingTime = remaining + seconds;
    let calculatedRowIndex = rowIndex;

    if (seconds < 0) {
      while (calculatedRemainingTime < 0 && calculatedRowIndex < currentRows.length - 1) {
        const underflow = -calculatedRemainingTime;
        calculatedRowIndex++;
        const nextLevelTime = timeStringToSeconds(currentRows[calculatedRowIndex]?.time);
        calculatedRemainingTime = nextLevelTime - underflow;
      }
    } else if (seconds > 0) {
      const maxTimeForCurrentLevel = timeStringToSeconds(currentRows[calculatedRowIndex]?.time);
      if (calculatedRemainingTime > maxTimeForCurrentLevel && maxTimeForCurrentLevel > 0) {
        calculatedRemainingTime = maxTimeForCurrentLevel;
      }
    }

    const finalRemainingTime = Math.max(0, calculatedRemainingTime);
    const newTimerState = { ...currentState.timer_state };
    newTimerState.remaining = finalRemainingTime;
    newTimerState.rowIndex = calculatedRowIndex;

    if (state === 'play') {
      newTimerState.endsAt = new Date(timeSyncService.getNow() + finalRemainingTime * 1000).toISOString();
      newTimerState.pausedAt = null;
    } else {
      newTimerState.pausedAt = finalRemainingTime;
      newTimerState.endsAt = null;
    }

    if (state !== 'play' && finalRemainingTime > 0) newTimerState.state = 'pause';
    if (finalRemainingTime === 0 && calculatedRowIndex >= currentRows.length - 1) newTimerState.state = 'stop';

    setTournamentState(prev => ({ ...prev, timer_state: newTimerState }));
    const result = await syncTimerToDB(newTimerState);
    handleSyncResult(result);
    resetWorker(finalRemainingTime);

    if (newTimerState.state === 'play') startWorker(finalRemainingTime);
    if (newTimerState.state === 'stop') stopWorker();
  }, [setTournamentState, syncTimerToDB, resetWorker, startWorker, stopWorker, timeStringToSeconds, stateRef, handleSyncResult]);

  return {
    startTimer,
    pauseTimer,
    resetTimer,
    adjustTimer
  };
};
